import { ApolloClient, InMemoryCache, createHttpLink, from } from '@apollo/client'
import { setContext } from '@apollo/client/link/context'
import { onError } from '@apollo/client/link/error'
import { removeTypenameFromVariables } from '@apollo/client/link/remove-typename'
import { GRAPHQL_URL } from '../config'

/**
 * Builds an Apollo client for Core, shared by the shopper embed and the brand-admin page — they
 * differ only in where the session token lives and what "logged out" means. Link order (v1 parity):
 *   1. removeTypename — strips __typename from variables
 *   2. errorLink      — a "Not Authorised" error from Core calls `onUnauthorised`
 *   3. authLink       — attaches `getToken()` as the `authorization` header, read per request so a
 *                       fresh login is picked up without re-instantiating the client
 *   4. httpLink       — POSTs to Core's /api/graphql
 */
export function createApolloClient(opts: { getToken: () => string | null; onUnauthorised: () => void }) {
  const authLink = setContext((_operation, { headers }) => {
    const token = opts.getToken()
    return { headers: { ...headers, ...(token ? { authorization: token } : {}) } }
  })

  const errorLink = onError(({ graphQLErrors }) => {
    if (graphQLErrors?.some((e) => e.message?.startsWith('Not Authorised'))) {
      opts.onUnauthorised()
    }
  })

  return new ApolloClient({
    cache: new InMemoryCache(),
    link: from([
      removeTypenameFromVariables(),
      errorLink,
      authLink,
      createHttpLink({ uri: GRAPHQL_URL }),
    ]),
    defaultOptions: {
      query: { errorPolicy: 'all' },
      mutate: { errorPolicy: 'all' },
      watchQuery: { errorPolicy: 'all' },
    },
  })
}
